import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { eventsAPI } from '@/services';
import { useToast } from '../components/Toast';
import { useConfirmDialog } from '../components/ConfirmDialog';
import { usePermissions } from '../hooks/usePermissions';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import ModalShell from '../components/ModalShell';
import { useSettings } from '../contexts/SettingsContext';
import { useAuth } from '../contexts/AuthContext';

const Calendar = () => {
  const { t, i18n } = useTranslation('common');
  const localeDate = (i18n.resolvedLanguage || i18n.language || 'fr').startsWith('en') ? 'en-US' : 'fr-FR';
  const { hasPermission } = usePermissions();
  const { error, success } = useToast();
  const { confirm } = useConfirmDialog();
  const { settings } = useSettings();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [showAllPastEvents, setShowAllPastEvents] = useState(false);
  // Récupérer les configurations depuis les settings
  const eventTypes = settings?.events?.event_types || ['meeting', 'deadline', 'event'];
  const meetingModes = settings?.events?.meeting_modes || ['présentiel', 'en ligne', 'hybride'];
  const defaultEventType = settings?.events?.default_event_type || 'meeting';
  const defaultMeetingMode = settings?.events?.default_meeting_mode || 'présentiel';

  const eventTypeLabel = (type) => {
    if (type === 'meeting') return t('calendar.typeMeeting');
    if (type === 'deadline') return t('calendar.typeDeadline');
    return t('calendar.typeEvent');
  };

  const meetingModeLabel = (mode) => {
    const keyByMode = {
      présentiel: 'calendar.modeInPerson',
      'en ligne': 'calendar.modeOnline',
      hybride: 'calendar.modeHybrid',
    };
    const key = keyByMode[mode];
    return key ? t(key) : mode;
  };

  const showOnlineMeetingLinkField = (mode) => mode === 'en ligne' || mode === 'hybride';

  /** Ligne issue de GET /agora/activities (workspace) — pas de fiche `/events/:id` ni CRUD via /agora/events. */
  const isAgoraActivityRow = (e) => Boolean(e && e._calendarSource === 'activity');
  const openDetails = (event) => {
    if (isAgoraActivityRow(event)) {
      navigate(`/activities/${event.id}`);
      return;
    }
    if (isAuthenticated) {
      navigate(`/events/${event.id}`);
    } else {
      navigate(`/events/public/${event.id}`);
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    location: '',
    type: defaultEventType,
    meeting_mode: '',
    meeting_link: '',
    poster_url: '',
    is_public: true,
    is_free: true,
    price: '',
    registration_open: true
  });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const response = await eventsAPI.getAll();
      const eventsList = Array.isArray(response.data)
        ? response.data
        : (response.data?.events || []);
      setEvents(eventsList);
    } catch (err) {
      console.error('Failed to load events:', err);
      error(t('calendar.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const buildApiPayloadFromForm = (rawFormData) => ({
    title: rawFormData.title,
    description: rawFormData.description || null,
    planned_date: rawFormData.start_date || null,
    location: rawFormData.location || null,
    event_type: rawFormData.type || 'event',
    meeting_mode: rawFormData.type === 'meeting' ? (rawFormData.meeting_mode || null) : null,
    meeting_link: rawFormData.meeting_link || null,
    poster_url: rawFormData.poster_url || null,
    is_public: Boolean(rawFormData.is_public),
    is_free: Boolean(rawFormData.is_free),
    price: rawFormData.is_free ? null : (rawFormData.price ? parseFloat(rawFormData.price) : null),
    registration_open: Boolean(rawFormData.registration_open),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const dataToSend = buildApiPayloadFromForm(formData);
      
      if (selectedEvent) {
        if (isAgoraActivityRow(selectedEvent)) {
          await eventsAPI.updateActivity(selectedEvent.initiative_id, selectedEvent.id, dataToSend);
          success(t('calendar.activityUpdated'));
        } else {
          await eventsAPI.update(selectedEvent.id, dataToSend);
          success(t('calendar.eventUpdated'));
        }
      } else {
        if (dataToSend.event_type === 'event') {
          await eventsAPI.create(dataToSend);
          success(t('calendar.eventCreated'));
        } else {
          await eventsAPI.createActivity(dataToSend);
          success(t('calendar.activityCreated'));
        }
      }
      
      loadEvents();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save event:', err);
      error(err.response?.data?.detail || t('calendar.saveError'));
    }
  };

  const handleDelete = async (eventRow) => {
    const confirmed = await confirm({
      title: isAgoraActivityRow(eventRow) ? t('calendar.deleteActivityTitle') : t('calendar.deleteEventTitle'),
      message: isAgoraActivityRow(eventRow) ? t('calendar.deleteActivityMessage') : t('calendar.deleteEventMessage'),
      type: 'danger'
    });
    
    if (!confirmed) return;
    
    try {
      if (isAgoraActivityRow(eventRow)) {
        await eventsAPI.deleteActivity(eventRow.initiative_id, eventRow.id);
        success(t('calendar.activityDeleted'));
      } else {
        await eventsAPI.delete(eventRow.id);
        success(t('calendar.eventDeleted'));
      }
      loadEvents();
    } catch (err) {
      console.error('Failed to delete event:', err);
      error(err.response?.data?.detail || t('calendar.deleteError'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      start_date: '',
      end_date: '',
      location: '',
      type: defaultEventType,
      meeting_mode: '',
      meeting_link: '',
      poster_url: '',
      is_public: true,
      is_free: true,
      price: '',
      registration_open: true
    });
    setSelectedEvent(null);
  };

  const handlePosterUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedEvent) {
      error(t('calendar.posterNeedEvent'));
      return;
    }

    setUploadingPoster(true);
    try {
      const response = await eventsAPI.uploadPoster(selectedEvent.id, file);
      setFormData({ ...formData, poster_url: response.data.poster_url });
      success(t('calendar.posterUploaded'));
      loadEvents();
    } catch (err) {
      error(err.response?.data?.detail || t('calendar.posterUploadError'));
    } finally {
      setUploadingPoster(false);
      event.target.value = '';
    }
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'meeting':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'deadline':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'event':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const toDayDate = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const upcomingEvents = events.filter((e) => {
    const day = toDayDate(e.start_date);
    return day ? day >= startOfToday : false;
  });
  const pastEvents = events.filter((e) => {
    const day = toDayDate(e.start_date);
    return day ? day < startOfToday : false;
  });
  const visiblePastEvents = showAllPastEvents ? pastEvents : pastEvents.slice(0, 5);
  const upcomingCount = upcomingEvents.length;
  const pastCount = pastEvents.length;

  if (loading) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center bg-gradient-to-b from-[#f2f6fb] to-[#eef3f8] py-16">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-[#113a5b]" />
        <p className="mt-4 text-sm text-slate-600">{t('calendar.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] space-y-3 sm:space-y-4 md:space-y-6">
      {/* Header */}
      <MemberMobileShell>
        <MemberMobileHero
          label={t('calendar.pageKicker')}
          title={
            upcomingEvents[0]
              ? upcomingEvents[0].title || t('calendar.sectionUpcoming')
              : t('calendar.sectionUpcoming')
          }
          hint={
            upcomingEvents[0]
              ? new Date(upcomingEvents[0].start_date).toLocaleDateString(localeDate, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : t('calendar.statUpcoming') + ` · ${upcomingCount}`
          }
          tone={upcomingCount > 0 ? 'info' : 'neutral'}
          actions={
            <>
              {upcomingEvents[0] ? (
                <Link
                  to={`/events/${upcomingEvents[0].id}`}
                  className="mm-cta flex-1"
                >
                  {t('calendar.openEvent')}
                </Link>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowForm(true);
                }}
                className="mm-cta mm-cta--ghost flex-1"
              >
                {t('calendar.createShort')}
              </button>
            </>
          }
        />
      </MemberMobileShell>

      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 md:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{t('calendar.desktopTitle')}</h1>
            <p className="text-xs sm:text-sm text-gray-600">{t('calendar.desktopSubtitle')}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowPastEvents((v) => !v)}
              className="px-4 py-2.5 min-h-[44px] border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm md:text-base whitespace-nowrap"
            >
              {showPastEvents ? t('calendar.hidePastDesktop') : t('calendar.showPastDesktop')}
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="px-4 py-2.5 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm md:text-base whitespace-nowrap"
            >
              {t('calendar.newEventButton')}
            </button>
          </div>
        </div>
      </div>

      {/* Upcoming Events */}
      <div>
        <h2 className="mb-2 px-0 text-sm font-bold text-gray-900 md:mb-4 md:px-1 md:text-xl">
          {t('calendar.sectionUpcoming')}
        </h2>
        <div className="grid gap-2.5 md:gap-4">
          {upcomingEvents.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-center md:rounded-lg md:p-8">
              <svg className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="mt-2 text-xs md:text-sm font-medium text-gray-900">{t('calendar.emptyUpcomingTitle')}</h3>
              <p className="mt-1 text-xs md:text-sm text-gray-500">{t('calendar.emptyUpcomingHint')}</p>
            </div>
          ) : (
            upcomingEvents.map((event) => (
              <div key={event.id} className={`overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm md:rounded-2xl md:border-0 md:shadow-xl border-l-4 ${getEventTypeColor(event.type)} transition-all md:hover:shadow-2xl`}>
                <div className="flex flex-col md:flex-row">
                  {/* Affiche à gauche */}
                  {event.poster_url && (
                    <div className="md:w-32 lg:w-40 flex-shrink-0 overflow-hidden bg-gray-100 flex items-center">
                      <img 
                        src={process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}${event.poster_url}` : event.poster_url}
                        alt={event.title}
                        className="w-full h-full object-cover cursor-pointer max-h-[120px] md:max-h-none"
                        onClick={() => openDetails(event)}
                      />
                    </div>
                  )}
                  
                  {/* Détails à droite */}
                  <div className={`flex-1 p-2.5 md:p-6 ${event.poster_url ? 'border-gray-200 md:border-l' : ''}`}>
                <div className="flex justify-between items-start gap-2 md:gap-3">
                  <div className="min-w-0 flex-1">
                        <div className="mb-1.5 flex flex-wrap items-center gap-1.5 md:mb-2 md:gap-2">
                          <h3 
                            className="min-w-0 flex-1 cursor-pointer text-[15px] font-semibold leading-snug text-gray-900 transition hover:text-entrea-royal md:break-words md:text-lg"
                            onClick={() => openDetails(event)}
                          >
                            {event.title}
                          </h3>
                      <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold md:px-3 md:py-1 md:text-xs ${getEventTypeColor(event.type)}`}>
                        {eventTypeLabel(event.type)}
                      </span>
                          {!event.is_public && (
                            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-800 md:px-3 md:py-1 md:text-xs">
                              {t('calendar.badgePrivate')}
                            </span>
                          )}
                          {!event.is_free && event.price && (
                            <span className="shrink-0 rounded-full bg-yellow-100 px-2 py-0.5 text-[10px] font-semibold text-yellow-800 md:px-3 md:py-1 md:text-xs">
                              {event.price} BIF
                            </span>
                          )}
                    </div>
                    {event.description && (
                      <p className="mb-2 line-clamp-2 text-[11px] text-gray-600 md:mb-3 md:text-sm">{event.description}</p>
                    )}
                    <div className="flex flex-col gap-1.5 text-[11px] text-gray-500 md:flex-row md:flex-wrap md:gap-4 md:text-sm">
                      <span className="flex items-center gap-1.5 md:gap-2">
                        <svg className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="break-words">
                          <span className="hidden md:inline">{new Date(event.start_date).toLocaleDateString(localeDate, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                          <span className="md:hidden">{new Date(event.start_date).toLocaleDateString(localeDate, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          {event.end_date && (
                            <>
                              <span className="hidden md:inline"> - {new Date(event.end_date).toLocaleDateString(localeDate)}</span>
                              <span className="md:hidden"> - {new Date(event.end_date).toLocaleDateString(localeDate, { day: 'numeric', month: 'short' })}</span>
                            </>
                          )}
                        </span>
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1.5 md:gap-2">
                          <svg className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          <span className="truncate">{event.location}</span>
                        </span>
                      )}
                          {event.type === 'meeting' && event.meeting_mode && (
                            <span className="flex items-center gap-1.5 md:gap-2">
                              <svg className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                              </svg>
                              <span className="truncate">{meetingModeLabel(event.meeting_mode)}</span>
                            </span>
                          )}
                          {event.meeting_link && (
                            <span className="flex items-center gap-1.5 md:gap-2">
                              <svg className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                              </svg>
                              <a href={event.meeting_link} target="_blank" rel="noopener noreferrer" className="text-entrea-royal hover:text-entrea-navy truncate">
                                {t('calendar.meetingLink')}
                              </a>
                            </span>
                          )}
                    </div>
                  </div>
                  <div className="flex gap-1 md:gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => openDetails(event)}
                          className="p-1.5 md:p-2 text-entrea-royal hover:bg-entrea-panel rounded-lg transition min-h-[36px] min-w-[36px] md:min-h-[40px] md:min-w-[40px] flex items-center justify-center"
                          title={isAgoraActivityRow(event) ? t('calendar.titleViewActivityDetails') : t('calendar.titleViewDetails')}
                        >
                          <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => navigate(`/meetings?event_id=${encodeURIComponent(event.id)}`)}
                          className="p-1.5 md:p-2 text-cyan-700 hover:bg-cyan-50 rounded-lg transition min-h-[36px] min-w-[36px] md:min-h-[40px] md:min-w-[40px] flex items-center justify-center"
                          title={event.type === 'meeting' ? t('calendar.titleManageMinutes') : t('calendar.titleManageActivityReport')}
                        >
                          <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m-7 4h10a2 2 0 002-2V8l-6-6H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </button>
                    {hasPermission('events.edit') && (
                    <button
                      onClick={() => {
                        setSelectedEvent(event);
                              setFormData({
                                ...event,
                                meeting_mode: event.meeting_mode || '',
                                meeting_link: event.meeting_link || '',
                                poster_url: event.poster_url || '',
                                is_public: event.is_public !== undefined ? event.is_public : true,
                                is_free: event.is_free !== undefined ? event.is_free : true,
                                price: event.price || '',
                                registration_open: event.registration_open !== undefined ? event.registration_open : true
                              });
                        setShowForm(true);
                      }}
                      className="p-1.5 md:p-2 text-entrea-royal hover:bg-entrea-panel rounded-lg transition min-h-[36px] min-w-[36px] md:min-h-[40px] md:min-w-[40px] flex items-center justify-center"
                        title={t('calendar.titleEdit')}
                    >
                      <svg className="h-4 w-4 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                    )}
                    {hasPermission('events.delete') && (
                    <button
                      onClick={() => handleDelete(event)}
                      className="p-1.5 md:p-2 text-red-600 hover:bg-red-50 rounded-lg transition min-h-[36px] min-w-[36px] md:min-h-[40px] md:min-w-[40px] flex items-center justify-center"
                        title={t('calendar.titleDelete')}
                    >
                      <svg className="h-4 w-4 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                    )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Past Events */}
      {showPastEvents && (
        <div>
          <div className="mb-2 flex items-center justify-between px-0 md:mb-4 md:px-1">
            <h2 className="text-sm font-bold text-gray-900 md:text-xl">{t('calendar.sectionPast')}</h2>
            {pastEvents.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllPastEvents((v) => !v)}
                className="text-sm font-medium text-entrea-royal hover:text-entrea-navy"
              >
                {showAllPastEvents ? t('calendar.showLess') : t('calendar.showAll', { count: pastEvents.length })}
              </button>
            )}
          </div>
          <div className="grid gap-2.5 md:gap-4">
            {visiblePastEvents.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-4 text-center text-xs text-gray-500 md:p-6 md:text-sm">
                {t('calendar.emptyPast')}
              </div>
            ) : visiblePastEvents.map((event) => (
              <div key={event.id} className="rounded-xl border border-gray-200 bg-gray-50/90 p-2.5 opacity-90 md:p-6">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base md:text-lg font-semibold text-gray-700 mb-2 break-words">{event.title}</h3>
                    <div className="flex flex-wrap gap-2 md:gap-4 text-xs md:text-sm text-gray-500">
                      <span className="flex items-center gap-1.5 md:gap-2">
                        <svg className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="hidden md:inline">{new Date(event.start_date).toLocaleDateString(localeDate, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                        <span className="md:hidden">{new Date(event.start_date).toLocaleDateString(localeDate, { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDelete(event)}
                    className="p-1.5 md:p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition min-h-[36px] min-w-[36px] md:min-h-[40px] md:min-w-[40px] flex items-center justify-center flex-shrink-0"
                  >
                    <svg className="h-4 w-4 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal Form */}
      {showForm && (
        <ModalShell
          maxWidthClass="max-w-2xl"
          panelClassName="flex min-h-0 flex-col overflow-hidden p-0"
          onBackdropClick={() => {
            setShowForm(false);
            resetForm();
          }}
        >
          <div className="mm-modal-header">
            <div className="min-w-0">
              <p className="mm-hero__label text-[#1f6ab8]">{t('calendar.pageTitle', 'Calendrier')}</p>
              <h2 className="mt-0.5 font-display text-lg font-extrabold tracking-tight text-[#0f3350]">
                {selectedEvent ? t('calendar.modalEditEvent') : t('calendar.modalNewEvent')}
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="mm-press flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100/80 text-slate-500"
              aria-label={t('calendar.cancel')}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

            <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-3 overflow-y-auto px-4 py-3 sm:space-y-4 sm:px-5 sm:py-4">
              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelTitle')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mm-field"
                  required
                  placeholder={t('calendar.placeholderTitle')}
                />
              </div>

              <div>
                <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelDescription')}</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="mm-field"
                  rows={3}
                  placeholder={t('calendar.placeholderDescription')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelStartDate')}</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mm-field"
                    required
                  />
                </div>

                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelEndDate')}</label>
                  <input
                    type="date"
                    value={formData.end_date || ''}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mm-field"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelLocation')}</label>
                  <input
                    type="text"
                    value={formData.location || ''}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    className="mm-field"
                    placeholder={t('calendar.placeholderLocation')}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelType')}</label>
                  <select
                    value={formData.type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData({ 
                        ...formData, 
                        type: newType,
                        // Réinitialiser meeting_mode si ce n'est pas une réunion
                        meeting_mode: newType === 'meeting' ? (formData.meeting_mode || defaultMeetingMode) : ''
                      });
                    }}
                    className="mm-field"
                    required
                  >
                    {eventTypes.map(type => (
                      <option key={type} value={type}>
                        {eventTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mode de réunion et lien (uniquement pour les réunions) */}
              {formData.type === 'meeting' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelMeetingMode')}</label>
                    <select
                      value={formData.meeting_mode || defaultMeetingMode}
                      onChange={(e) => setFormData({ ...formData, meeting_mode: e.target.value })}
                      className="mm-field"
                    >
                      {meetingModes.map(mode => (
                        <option key={mode} value={mode}>{meetingModeLabel(mode)}</option>
                      ))}
                    </select>
                  </div>

                  {showOnlineMeetingLinkField(formData.meeting_mode) && (
                    <div>
                      <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelMeetingLink')}</label>
                      <input
                        type="url"
                        value={formData.meeting_link || ''}
                        onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                        className="mm-field"
                        placeholder={t('calendar.placeholderMeetingLink')}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Affiche */}
              {selectedEvent && (
                <div>
                  <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelPoster')}</label>
                  {formData.poster_url ? (
                    <div className="mb-2">
                      <img 
                        src={process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}${formData.poster_url}` : formData.poster_url}
                        alt={t('calendar.posterAlt')}
                        className="w-full max-h-48 object-cover rounded-lg border border-gray-300"
                      />
                    </div>
                  ) : null}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePosterUpload}
                    disabled={uploadingPoster || !selectedEvent}
                    className="mm-field text-sm"
                  />
                  {uploadingPoster && (
                    <p className="mt-1 text-sm text-gray-500">{t('calendar.uploadInProgress')}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">{t('calendar.posterHint')}</p>
                </div>
              )}

              {/* Paramètres d'inscription */}
              <div className="border-t border-slate-200/90 pt-4">
                <h3 className="mb-4 font-display text-sm font-bold text-[#0f3350]">{t('calendar.registrationSection')}</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_public"
                      checked={formData.is_public}
                      onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                      className="w-4 h-4 text-entrea-royal border-gray-300 rounded focus:ring-entrea-royal/40"
                    />
                    <label htmlFor="is_public" className="text-sm font-medium text-gray-700">
                      {t('calendar.checkboxPublic')}
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="registration_open"
                      checked={formData.registration_open}
                      onChange={(e) => setFormData({ ...formData, registration_open: e.target.checked })}
                      className="w-4 h-4 text-entrea-royal border-gray-300 rounded focus:ring-entrea-royal/40"
                    />
                    <label htmlFor="registration_open" className="text-sm font-medium text-gray-700">
                      {t('calendar.checkboxRegistrationOpen')}
                    </label>
                  </div>

                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="is_free"
                      checked={formData.is_free}
                      onChange={(e) => {
                        const newIsFree = e.target.checked;
                        setFormData({ 
                          ...formData, 
                          is_free: newIsFree,
                          price: newIsFree ? '' : formData.price
                        });
                      }}
                      className="w-4 h-4 text-entrea-royal border-gray-300 rounded focus:ring-entrea-royal/40"
                    />
                    <label htmlFor="is_free" className="text-sm font-medium text-gray-700">
                      {t('calendar.checkboxFree')}
                    </label>
                  </div>

                  {!formData.is_free && (
                    <div>
                      <label className="mb-1.5 block font-display text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{t('calendar.labelPrice')}</label>
                      <input
                        type="number"
                        value={formData.price || ''}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        className="mm-field"
                        placeholder="0"
                        min="0"
                        step="0.01"
                        required={!formData.is_free}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

              <div className="mm-modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="mm-cta mm-cta--ghost flex-1"
                >
                  {t('calendar.cancel')}
                </button>
                <button type="submit" className="mm-cta flex-1">
                  {selectedEvent ? t('calendar.submitUpdate') : t('calendar.submitCreate')}
                </button>
              </div>
            </form>
        </ModalShell>
      )}
    </div>
  );
};

export default Calendar;